import React, { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import Step1 from "./Step1";
import Step2 from "./Step2";
import Step3 from "./Step3";
import Step5 from "./Step5";
import Step6 from "./Step6";
import axios from "axios";
import toastr from "toastr";
import "toastr/build/toastr.min.css";
import { useNavigate } from "react-router-dom";

const StepsWrapper = () => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState("step1");
  const [userInfo, setUserInfo] = useState({});
  const [stats, setStats] = useState({});
  const navigate = useNavigate();
  const hasCheckedFormRef = React.useRef(false);

  useEffect(() => {
    fetchUserInfo();
    fetchStats();
  }, []);

  useEffect(() => {
    fetchStats();
  }, [currentStep]);

  const fetchUserInfo = async () => {
    try {
      const email = localStorage.getItem('email');
      setUserInfo({ email, name: localStorage.getItem('name') || 'User' });
    } catch (error) {
      console.error('Error fetching user info:', error);
    }
  };

  const fetchStats = async () => {
    try {
      // Get current step progress
      const currentStepIndex = getCurrentStepIndex(currentStep);
      const completedSteps = currentStepIndex;
      const totalSteps = steps.length;
      const completionRate = Math.round((completedSteps / totalSteps) * 100);

      setStats({
        completedSteps,
        totalSteps,
        completionRate,
        lastUpdated: new Date().toLocaleDateString(),
        currentStep: currentStepIndex + 1
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const getCurrentStepIndex = (step) => {
    const stepMap = {
      'step1': 0,
      'step2': 1,
      'step3': 2,
      'step5': 3,
      'step6': 4
    };
    return stepMap[step] || 0;
  };

  const handleStepClick = (stepKey) => {
    const stepIndex = getCurrentStepIndex(stepKey);
    const currentStepIndex = getCurrentStepIndex(currentStep);

    // Only allow navigation to completed steps or the next step
    if (stepIndex <= currentStepIndex) {
      setCurrentStep(stepKey);
    }
  };

  const getStepKey = (index) => {
    return index < 3 ? `step${index + 1}` : `step${index + 2}`;
  };

  const getStepStatus = (index) => {
    const currentStepIndex = getCurrentStepIndex(currentStep);
    if (index < currentStepIndex) return 'completed';
    if (index === currentStepIndex) return 'current';
    return 'pending';
  };


  const steps = [
    t('steps.basicInformation'),
    t('steps.sector'),
    t('steps.industry'),
    t('steps.field'),
    t('steps.completion'),
  ];

  const checkIfFormFinished = async () => {
    // Prevent duplicate calls
    if (hasCheckedFormRef.current) {
      return;
    }
    
    hasCheckedFormRef.current = true;
    
    try {
      const response = await axios.post('/is-form-finished');
      
      if (response.data.status && response.data.isFormFinished) {
        toastr.success(response.data.message || t('steps.formAlreadyCompleted'));
        navigate("/questions");
      }
    } catch (error) {
      console.error('Error checking form status:', error);
      toastr.error(t('steps.failedToCheckFormStatus'));
      hasCheckedFormRef.current = false; // Reset on error so they can retry
    }
  };

  useEffect(() => {
    checkIfFormFinished();
    fetchUserInfo();
    fetchStats();
  }, []);



  return (
    <div className="min-h-screen bg-gray-50 font-sans antialiased">
      {/* Compact Welcome Banner */}
      <div className="bg-gradient-to-r from-emerald-600 via-green-600 to-teal-600 shadow-lg relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 py-4 relative z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center shadow">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white mb-1">{t('steps.welcomeBack')}, {userInfo.name}!</h1>
                <p className="text-green-100 text-sm">{t('steps.completeKycProcess')}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-xs text-green-100">{t('steps.lastUpdated')}: {stats.lastUpdated}</p>
              <p className="text-sm text-lime-300 font-bold">{stats.completionRate || 0}{t('steps.percentComplete')}</p>
              <p className="text-xs text-green-200">{t('steps.stepOf', { current: stats.currentStep || 1, total: stats.totalSteps || 5 })}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Compact Stats Cards */}
      <div className="max-w-full mx-auto px-4 py-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          {/* Progress Card */}
          <div className="bg-white rounded-lg shadow border border-gray-100 p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600 mb-1">{t('answers.progress')}</p>
                <p className="text-xl font-bold text-blue-600">{stats.completedSteps || 0}/{stats.totalSteps || 5}</p>
                <div className="text-xs text-gray-500">{t('steps.stepsCompleted')}</div>
              </div>
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>

          {/* Completion Rate Card */}
          <div className="bg-white rounded-lg shadow border border-gray-100 p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600 mb-1">{t('reporting.completionRate')}</p>
                <p className="text-xl font-bold text-purple-600">{stats.completionRate || 0}%</p>
                <div className="text-xs text-gray-500">{t('steps.overallProgress')}</div>
              </div>
              <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
            </div>
          </div>

          {/* Status Card */}
          <div className="bg-white rounded-lg shadow border border-gray-100 p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600 mb-1">{t('answers.status')}</p>
                <p className="text-lg font-bold text-orange-600">
                  {stats.completionRate === 100 ? t('steps.completed') :
                    stats.completionRate > 0 ? t('steps.inProgress') : t('steps.notStarted')}
                </p>
                <div className="text-xs text-gray-500">{t('steps.kycVerification')}</div>
              </div>
              <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Compact KYC Process Container */}
        <div className="bg-white rounded-lg shadow border border-gray-100 overflow-hidden">
          <div className="bg-gradient-to-r from-green-600 to-green-700 px-4 py-3">
            <h2 className="text-white text-base font-semibold flex items-center">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {t('steps.kycProcessSteps')}
            </h2>
            <p className="text-green-100 text-xs">{t('steps.completeAllSteps')}</p>
          </div>

          <div className="p-3">
            {/* Compact Stepper Navigation */}
            <div className="flex justify-between w-full mb-4">
              {steps.map((step, index) => {
                const stepKey = getStepKey(index);
                const stepStatus = getStepStatus(index);
                const isCompleted = stepStatus === 'completed';
                const isCurrent = stepStatus === 'current';
                const isClickable = stepStatus === 'completed' || stepStatus === 'current';

                return (
                  <div
                    key={index}
                    className={`flex-1 text-center relative mr-2 ${isClickable ? 'cursor-pointer' : 'cursor-not-allowed'}`}
                    onClick={() => isClickable && handleStepClick(stepKey)}
                  >
                    <div className="flex flex-col items-center">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center mb-1 ${isCompleted ? 'bg-green-500 text-white hover:bg-green-600' :
                        isCurrent ? 'bg-green-100 text-green-600 border border-green-500 hover:bg-green-200' :
                          'bg-gray-100 text-gray-400'
                        }`}>
                        {isCompleted ? (
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        ) : (
                          <span className="text-xs font-semibold">{index + 1}</span>
                        )}
                      </div>
                      <span className={`text-xs transition-colors ${isCurrent ? "text-green-600 font-medium" : isCompleted ? "text-green-500" : "text-gray-400"
                        }`}>
                        {step}
                      </span>
                    </div>
                    {index < steps.length - 1 && (
                      <div className={`absolute top-3 left-full w-full h-0.5 transform translate-x-1 ${isCompleted ? 'bg-green-500' : 'bg-gray-200'
                        }`} style={{ width: 'calc(100% - 0.5rem)' }}></div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Step Content */}
            <div className="mt-3">
              {currentStep === "step1" && <Step1 onNext={() => setCurrentStep("step2")} />}
              {currentStep === "step2" && <Step2 onNext={() => setCurrentStep("step3")} />}
              {currentStep === "step3" && (
                <Step3 isActive={currentStep === "step3"} onNext={() => setCurrentStep("step5")} />
              )}
              {currentStep === "step5" && (
                <Step5 isActive={currentStep === "step5"} onNext={() => setCurrentStep("step6")} />
              )}
              {currentStep === "step6" && (
                <Step6 onComplete={() => isFormFinished()} />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  async function isFormFinished() {
    try {
      const response = await axios.post('/is-form-finished');
      if (response.data.status) {
        if (response.data.isFormFinished) {
          toastr.success(response.data.message || t('steps.formCompletedSuccessfully'));
          navigate("/questions");  // Corrected navigation
        } else {
          toastr.warning(t('steps.formNotFinished'));
        }
      } else {
        toastr.error(response.data.message || t('answers.answerSaveError'));
      }
    } catch (error) {
      toastr.error(t('steps.errorOccurredCheckingForm'));
    }
  }
};

export default StepsWrapper;
